import type { PlayerProfileSnapshot } from '@goodtime/engine';
import { api, ApiError } from './client';

/**
 * Server profile-sync client (2026-06-09). Durable backup of the full
 * {@link PlayerProfileSnapshot} so game state survives a reinstall — the
 * client re-fetches under the (persisted) player id on launch and, if the
 * local cache is empty, rehydrates from this.
 *
 * <p>Opaque blob: the server stores/returns the snapshot without reading
 * into it, so adding a profile field needs zero server change. Last-write-
 * wins on {@code clientUpdated} (epoch ms) protects newer state from a
 * stale push. Currency (coins/gems) stays authoritative via the wallet
 * endpoints; this blob owns the rest (settings, unlocks, artefacts, streaks).
 */
export interface StoredProfile {
  profile: PlayerProfileSnapshot;
  /** Epoch ms of the snapshot the server currently holds. */
  clientUpdated: number;
}

/** GET the server-backed snapshot, or null if the player has never synced
 *  (the server replies 404 — treated here as "no backup, boot from local"). */
export async function getProfile(playerId: string): Promise<StoredProfile | null> {
  try {
    return await api<StoredProfile>(`/players/${encodeURIComponent(playerId)}/profile`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/** PUT (upsert) the snapshot. Stamps {@code clientUpdated = Date.now()};
 *  the server keeps whichever write is newest. */
export async function putProfile(
  playerId: string,
  profile: PlayerProfileSnapshot,
): Promise<void> {
  await api<StoredProfile>(`/players/${encodeURIComponent(playerId)}/profile`, {
    method: 'PUT',
    body: JSON.stringify({ profile, clientUpdated: Date.now() }),
  });
}
